// Shared value-type primitives used across every layout.
//
// Source of truth: wiki/framework_spec/13-layout-tag-catalogue.md §0.4 and §1.
// Each editor field has a value type — these are the typed equivalents.
import { z } from "zod";
/**
 * A CSS colour — a named colour (`black`, `white`, `red`), a hex value
 * (`#fff`, `#0f0f0f`), or an `rgb()` / `rgba()` value (`rgba(0,0,0,0.4)`).
 * Per catalogue §0.4.
 *
 * rgb()/rgba() is accepted because the Player injects the colour verbatim
 * into CSS (`dynamo-player/js/components.js:5922`) and the emitter passes it
 * through unchanged (`packages/player-xml-emitter/src/emit-layout.ts`) — a
 * semi-transparent title bar over video (`rgba(0,0,0,0.4)`) is a deliberate,
 * supported effect, not a value the schema should reject.
 */
export const ColorSchema = z
    .string()
    .min(1)
    .refine((v) => /^#[0-9a-fA-F]{3}$|^#[0-9a-fA-F]{6}$|^[a-zA-Z]+$|^rgba?\([\d.,%\s/]+\)$/.test(v.trim()), "color must be a named colour (e.g. 'black'), a hex value (e.g. '#fff'), or an rgb()/rgba() value");
/** An asset path, package-root-relative. Per catalogue §0.4. */
export const UrlSchema = z.string().min(1);
/**
 * Plain text — single-line, no rich-text formatting. The XML-emission layer
 * is responsible for CDATA wrapping.
 */
export const PlainTextSchema = z.string();
/**
 * Rich text — may contain the inline HTML tags listed in catalogue §2
 * (`<br> <b> <i> <h3> <ul> <li> <a>`). Authored inside `<![CDATA[ … ]]>`
 * at emit time. The schema does not parse the HTML; it trusts the
 * authoring editor (the `RichTextEditor` widget) to produce only the
 * documented inline subset.
 */
export const RichTextSchema = z.string();
/**
 * A localized string — one value per ISO language code. Authored via the
 * `LocalizedStringEditor` widget. Keys are language codes ("en", "it", …).
 */
export const LocalizedStringSchema = z.record(z.string(), z.string());
/**
 * Does this value carry words a human could translate?
 *
 * The one place that answers that question, used by the coverage walker (to
 * decide whether a field is UNUSED or UNTRANSLATED) and by the emitter's `pick`
 * (to decide whether an absent language is a defect or a non-event). Two callers
 * in two packages, so it lives here rather than being written twice —
 * a rule written twice diverges.
 *
 * Deliberately stricter than `length > 0`, because three shapes all mean "the
 * author left this blank" and only one of them is the empty string:
 *
 * - `"   "` — whitespace, usually a stray keystroke.
 * - `"<p></p>"` / `"<br>"` — what a browser's contentEditable leaves behind when
 *   a rich-text field is cleared. Visibly empty, and `"<br>".length === 4`.
 * - `"&nbsp;"` — survives a paste from Word and from most web pages.
 *
 * Tag-stripping by regex is safe HERE and is not a security control: the result
 * is compared against `""` and thrown away. Nothing is rendered from it.
 */
export function hasTranslatableText(value) {
    if (typeof value !== "string")
        return false;
    return (value
        .replace(/<[^>]*>/g, "")
        // `.trim()` already covers the non-breaking space and the BOM (both are
        // WhiteSpace per the spec), so only the ENTITY forms need spelling out —
        // at this point they are still six characters of text, not one space.
        .replace(/&nbsp;|&#0*160;|&#x0*a0;/gi, " ")
        .trim().length > 0);
}
/**
 * The marker that tells the translation-coverage walker "the runtime renders
 * this field only when it HAS text, so blank in EVERY language is a legitimate
 * authoring choice — an unused field, not a missing translation".
 *
 * Omar, 2026-08-10, on a `text_and_image` screen he wanted with no title bar:
 * *"I don't need the Title block and Description, therefore I left them empty
 * with no text. However when I try to export the zip I get … Missing text:
 * moduleGroups[0].modules[0].layouts[1].titleMain (en, it, ar). These fields can
 * be empty in some cases, therefore there shouldn't be a block for the export."*
 *
 * He is right, and the Player agrees — this is a RUNTIME fact, not a preference:
 * `components.js:_getCommonTemplate` renders the whole title block only when
 * `(layout.titleMain || layout.titleSub)` is truthy, so a blank title yields no
 * empty bar and no broken markup. Twelve of the thirteen layout templates route
 * their title through it; the thirteenth reads `layout.titleMain || ''`. The
 * shipped corpus says the same thing louder: `03_BT_CE` contains **18
 * `text_and_image` layouts and one `title` layout with no `<titleMain>` element
 * at all** — Omar's exact case is a pattern that has been in production for
 * years.
 *
 * **Why a marker and not a blanket rule.** "Blank everywhere ⇒ don't block"
 * cannot be applied to every LocalizedString, because some blanks are the very
 * thing a gate exists to catch. `courseSettings.dftiFlow`'s four screen texts
 * start deliberately empty in a blank course *so that* this gate makes the
 * author write their own intro (`data.jsx makeBlankCourse`); relaxing them would
 * ship a course whose second screen is a blank profile form. Same for an
 * assessment question with no prompt and a role with no label, each of which has
 * its own purpose-built gate. So the permission is declared at the FIELD, next
 * to the thing it describes, and the checker keeps one rule for everyone.
 *
 * **Why a marker is needed AT ALL, given `.optional()`.** The walker already
 * forgives a total blank on any `.optional()` field, because there absence is
 * legal and `{en: ""}` is the same authoring act as no key (see `blankIsAllowed`
 * in `coverage.ts`). That covers `titleSub`, a module's `info`, and every
 * optional feedback string — none of which need this marker, and none of which
 * carry it. What `.optional()` cannot express is a **required key whose text is
 * optional**: every layout HAS a title slot, so the key stays required, while the
 * text inside it is the author's choice. That distinction is the marker's whole
 * job, which is why it appears on exactly the thirteen required `titleMain`
 * fields and nowhere else.
 *
 * **A blank is only forgiven when it is total.** `{en: "Overview", it: ""}` is
 * still a gap on `it` — that is a real missing translation and export still
 * refuses it. Only "no text in any enabled language" is read as "unused".
 */
export const BLANKABLE_TEXT_MARKER = "blankable-text";
/**
 * A localized string the author may leave blank in every language.
 *
 * Identical runtime shape to {@link LocalizedStringSchema} — the marker rides in
 * `.describe()`, the only seam that survives the coverage walker's `unwrap()`
 * (see {@link LOCALIZED_URL_MARKER} for the alternatives that were measured and
 * rejected).
 *
 * Built from `z.record(...)` directly rather than as
 * `LocalizedStringSchema.describe(...)`: if anyone ever adds a `.refine()` to
 * `LocalizedStringSchema`, that expression would put the marker on a
 * `ZodEffects` WRAPPER, `unwrap()` would strip it, and every field here would
 * silently revert to blocking — a failure whose only symptom is a returning bug.
 */
export const BlankableLocalizedStringSchema = z
    .record(z.string(), z.string())
    .describe(BLANKABLE_TEXT_MARKER);
/**
 * The marker that tells the translation-coverage walker "this record is a set of
 * MEDIA references, not a set of translations".
 *
 * `findLocalizedCoverageGaps` identifies a translatable field purely by its Zod
 * signature — `ZodRecord<ZodString, ZodString>` (`coverage.ts`). A per-language
 * URL record has exactly that signature, so without this marker every localized
 * media field would be reported as a missing TRANSLATION and would gate the
 * export: a course with Italian enabled and no Italian subtitle would be refused
 * a build over an optional track.
 *
 * Measured, not assumed: `.min(1)` does NOT change the signature
 * (`z.string().min(1)._def.typeName === "ZodString"`), and `.brand()` produces a
 * `ZodBranded` the walker's `unwrap()` does not unwrap, so it falls into the
 * silent-skip default. `.describe()` is the only seam that both survives
 * `unwrap()` and stays inspectable.
 */
export const LOCALIZED_URL_MARKER = "localized-url";
/**
 * Marks a LocalizedString whose value is a PROPER NOUN — a name that does not
 * need translating, and usually must not be.
 *
 * Requested by Omar, 2026-08-12, on the organisation names: *"the check show the
 * following message that is not necessary as the Organisation names do not need
 * to be translated."* He is right, and the reference packages agree — BT's
 * organisations are `EE`, `Plusnet`, `Openreach`. Translating a company name is
 * usually wrong, not merely unnecessary.
 *
 * ★ BUT THE PLAYER STILL NEEDS ONE ENTRY PER ENABLED LANGUAGE, so this marker
 * cannot simply mean "skip it". `buildOneBrandImg` renders an organisation pill
 * only when `label_text[selected_language]` is defined
 * (`js/scripts.js:3889`) — an organisation with no Italian entry is offered to
 * NOBODY Italian, and every role inside it becomes unreachable at the same time.
 * So the contract is two-part and the halves must ship together:
 *
 *   1. the coverage gate does not ASK the author for a translation (this marker), and
 *   2. the emitter FILLS every enabled language from whichever value exists.
 *
 * Half of that alone is a defect: (1) without (2) ships a course that silently
 * hides organisations from most of its learners, and (2) without (1) leaves the
 * author blocked by a question they should never have been asked.
 *
 * Distinct from {@link BLANKABLE_TEXT_MARKER}, which means "blank everywhere is
 * *unused*" — a proper noun is very much used; it just has one spelling.
 */
export const PROPER_NOUN_MARKER = "proper-noun";
/**
 * A LocalizedString holding a proper noun — see {@link PROPER_NOUN_MARKER}.
 *
 * Same runtime shape as {@link LocalizedStringSchema}; the marker is what the
 * translation-coverage walker reads.
 */
export const ProperNounLocalizedStringSchema = z
    .record(z.string(), z.string())
    .describe(PROPER_NOUN_MARKER);
/**
 * Text the learner only ever sees in the course's FIRST language, because it is
 * rendered BEFORE the language picker exists.
 *
 * ★ Measured, not assumed. Once the content XML has loaded the Player does
 * `HShell.content.selected_languageObj = HShell.content.languageArray[0]`
 * (`js/scripts.js:6749`) and paints from there. `langSelect` — which assigns
 * `languageArray[$(this).attr('positionInArray')]` (`:7400`) — is a LATER segment
 * in every course template, and the cover screen is REMOVED when the learner
 * presses Start (`:7495`), so it never repaints in the language they chose.
 *
 * So a translation of this field is text no learner can reach. Omar, 2026-08-13,
 * on being blocked by exactly that:
 *
 *   > *"there is no need at the beginning of the course to run a localisation as
 *   > the language selection happen after the welcome screen. There is no need to
 *   > block it."*
 *
 * Distinct from {@link PROPER_NOUN_MARKER}, and the distinction is worth keeping:
 * a proper noun HAS one spelling everywhere; this text could be translated
 * perfectly well — there is simply no screen that would ever show the translation.
 * Recording the real reason means the next person can tell whether a new field
 * belongs here (rendered pre-`langSelect`) or there (a name).
 *
 * ⚠️ **Same two-part contract as `PROPER_NOUN_MARKER`, for a different reason.**
 * The gate must not ASK (this marker), *and* the emitter must FILL every enabled
 * language from whichever value exists — because UI.xml is written per language
 * and the Player reads whichever file matches `languageArray[0]`. Exempting the
 * field without the fill would ship a cover whose sentence is blank for any
 * course whose first language is not the one the author typed in.
 */
export const DEFAULT_LANGUAGE_ONLY_MARKER = "default-language-only";
/**
 * A LocalizedString rendered before the language picker — see
 * {@link DEFAULT_LANGUAGE_ONLY_MARKER}.
 *
 * Same runtime shape as {@link LocalizedStringSchema}; the marker is what the
 * translation-coverage walker reads. The author MAY still translate it on the
 * Localisation surface — they are simply never blocked for not having.
 */
export const DefaultLanguageOnlyStringSchema = z
    .record(z.string(), z.string())
    .describe(DEFAULT_LANGUAGE_ONLY_MARKER);
/**
 * Text the app ALREADY CARRIES in every language: the New to company screen's blocks, which the 15
 * UI.xml templates hold as professional translations.
 *
 * Omar, 2026-10-08: *"Export must treat a language without its own New to company wording as covered
 * by the app's built-in translation."* · *"Keep the built-in translations as they are, even if they
 * don't match today's English exactly."* So a language with no wording of its own is not MISSING: the
 * package keeps the built-in text for it (`emit-ui-xml.ts` replaces a template block only with that
 * language's own non-blank wording). Such a field is judged `covered`, which means:
 *   - the export gate never asks for it;
 *   - the main-language fill never copies into it. After an English change that copy would put the
 *     ENGLISH on an Italian learner's screen in place of the built-in Italian his rule keeps.
 *
 * ★ It is a VERDICT, not a skip. The walker still VISITS the field, because the Language Reviewer's
 * guard redacts each reviewer's own languages through the same walk
 * (`redactLocalizedValuesForLanguages`): a skipped field would keep an Italian reviewer's Italian edit
 * in the comparison and refuse it. Only the callers that act on a verdict change behaviour.
 *
 * ★ One half only, unlike {@link PROPER_NOUN_MARKER} and {@link DEFAULT_LANGUAGE_ONLY_MARKER}: nothing
 * `pick`s these fields (they reach the package only through UI.xml, one language at a time), so there
 * is no emitter fill to supply. Localisation still OFFERS them for translation once the main language
 * has wording of its own; that is an invitation, not a requirement.
 */
export const BUILT_IN_TRANSLATION_MARKER = "built-in-translation";
/**
 * A LocalizedString the app already carries in every language — see
 * {@link BUILT_IN_TRANSLATION_MARKER}. Same runtime shape as {@link LocalizedStringSchema}; the marker
 * is what the translation-coverage walker reads.
 */
export const BuiltInTranslatedStringSchema = z
    .record(z.string(), z.string())
    .describe(BUILT_IN_TRANSLATION_MARKER);
/**
 * The screen-reader description of a piece of AUTHOR-ADDED COURSE MEDIA.
 *
 * ★ **The role decides, not the shape** (Omar, 2026-09-02, `17_OPEN_QUESTIONS` OQ-095
 * D7): *"Course media authored by the user → accessible description. Interactive /
 * navigation element → accessible action label. Pure Player decoration → ignored by
 * the screen reader."* This schema is for the FIRST of those three only. It never
 * appears on Player chrome, and it is not how a control gets its name.
 *
 * **Per language, and never demanded.** Omar, same day: *"Store the accessibility
 * description **per language**, but do **not** require authors to translate it. The
 * English value should be used as the default/fallback across all languages. If
 * translated descriptions already exist, the system should be capable of preserving
 * and using them, but translation must remain optional and must not be part of the
 * normal translation-completeness gate."*
 *
 * The BT source is why the value is per-language rather than one plain string: its
 * descriptions really are translated (`SMEs` / `KMU` / `PME` / `PMI`), so a single
 * string would honour "no translation required" by DISCARDING translations that
 * already exist.
 *
 * ⚠️ **Two-part contract, exactly like {@link DEFAULT_LANGUAGE_ONLY_MARKER}, and the
 * second half is the one that gets forgotten.** The coverage gate must not ASK for
 * the other languages (this marker), *and* the build must FILL a blank language from
 * the default one. Exempting without filling is worse than not exempting: `pick`
 * throws when a field has text in one language and no key in another, so an
 * English-only description would reach the author as an opaque 500 on the Italian
 * package. The fill lives in `projectLocalizedStringsOntoEnabledLanguages`.
 *
 * Distinct from {@link BLANKABLE_TEXT_MARKER} (blank in EVERY language is fine, but
 * blank in SOME is still a gap — the opposite of what is wanted here) and from
 * {@link DEFAULT_LANGUAGE_ONLY_MARKER} (text the learner only ever sees in the first
 * language, because it is painted before the language picker exists; this text is
 * shown in whatever language the learner picked, it simply need not be translated).
 */
export const MEDIA_DESCRIPTION_MARKER = "media-description";
/**
 * A screen-reader description for author-added course media — see
 * {@link MEDIA_DESCRIPTION_MARKER}.
 *
 * Same runtime shape as {@link LocalizedStringSchema}; the marker is what the
 * coverage walker and the language fill both read. Always `.optional()` at its use
 * sites: a required field would refuse to save work in progress, and Omar's decision
 * D2 is explicit that nothing about this may block an export.
 */
export const MediaDescriptionSchema = z
    .record(z.string(), z.string())
    .describe(MEDIA_DESCRIPTION_MARKER);
/**
 * ★ The words a "Screen-reader description" holds until the author writes one (Omar, 2026-10-27, PR #400): *"Default
 * text: "Image" for pictures, "Video" for videos. If the author doesn't edit the box (or clears it), the default is the
 * main-language text, so it is never empty."* And: *"A language without its own text gets the main-language text
 * (German learners hear "Image"). Never another language's text."*
 *
 * So a description's text for a language is, in this order: its own; else the MAIN language's; else this default for
 * its kind. Nothing is stored for it: an empty or absent main-language description MEANS the default, read the same way
 * by the build (`projectLocalizedStringsOntoEnabledLanguages`, then the emitter's `mediaUrlTag`), the editor's box and
 * Localisation's row (`locDescriptionText`, apps/fe/src/loc-translate-core.js, pinned to this by
 * `fe-localisation-editor-names.test.ts`). A video's play control keeps its own words, "Play the video.", in the Player.
 */
export const MEDIA_DESCRIPTION_DEFAULT = { image: "Image", video: "Video" };
/**
 * A per-language MEDIA reference — one URL (or `asset://<id>`) per language code.
 *
 * The runtime shape is the same as {@link LocalizedStringSchema}, but the two are
 * deliberately distinct schemas rather than one reused type:
 *
 * 1. **A missing language is not a gap.** Subtitles are optional per language;
 *    a missing translation of body text is a defect. Only the marker above can
 *    tell those two apart, and only one of these schemas carries it.
 * 2. **There is exactly one way to say "no track for this language": omit the
 *    key.** The value type is {@link UrlSchema} (`.min(1)`), so `{it: ""}` is
 *    REJECTED. An empty-string husk would otherwise become a second encoding of
 *    "unset" that wins precedence tests and silently beats a real value — the
 *    failure this project has already paid for in text fields.
 *
 * `{}` is accepted and inert: the packager's flatten deletes the key and the
 * emitter omits the element. It is deliberately NOT `.refine`d to be non-empty —
 * a `custom` Zod issue inside `layouts` is classified as BLOCKING by the draft
 * route's unfinished-field triage, which would 400 an author's whole save over a
 * shape they cannot see.
 *
 * **A legacy plain string is ACCEPTED and normalised to `{en: value}`** rather
 * than rejected. Two reasons, both measured:
 *
 * - A production draft really does hold plain-string subtitle paths, and a
 *   strict record would 400 its next save over a value its author never set.
 * - Any API client that predates this change sends a string. Rejecting it makes
 *   this a breaking route contract; coercing it does not.
 *
 * Crucially the coercion happens at the BOUNDARY, so everything downstream sees
 * one shape. Accepting `string | record` as the *stored* type would instead keep
 * the defect this change removes permanently legal — a single string shared by
 * every language is exactly the bug — and would leave the invariant
 * unenforceable. An empty string normalises to the inert `{}` (see the note on
 * the `preprocess` below for why not `undefined`), which reaches the same end
 * state as absent: no key, no element.
 *
 * The description sits on the INNER record, not on the `preprocess` wrapper:
 * `unwrap()` in the coverage walker strips `ZodOptional` **and** `ZodEffects`, so
 * a marker on the outside would be discarded exactly where it is needed.
 */
const LocalizedUrlRecord = z
    .record(z.string(), UrlSchema)
    .describe(LOCALIZED_URL_MARKER);
/**
 * How a stored per-language media value is READ: a plain string is the legacy, English-only shape, and `""`
 * is unset. `LocalizedUrlSchema` applies it; so does {@link withLegacyLocalizedUrlsRead}, for a draft the
 * schema did not parse.
 *
 * `""` becomes `{}` rather than `undefined`: the outer `.optional()` at each field site only short-circuits
 * on an undefined INPUT, so returning undefined here would fail the inner record as "Required" instead of
 * meaning "unset". `{}` is inert end to end — the packager's flatten deletes the key and the emitter omits
 * the element — so it lands in exactly the same place as absent.
 */
export function readLegacyLocalizedUrl(value) {
    return typeof value === "string" ? (value === "" ? {} : { en: value }) : value;
}
export const LocalizedUrlSchema = z.preprocess(readLegacyLocalizedUrl, LocalizedUrlRecord);
/** Every field the schema declares as a `LocalizedUrlSchema`. Kept equal to the schema by
 *  `localized-url.test.ts`, which walks it. */
export const LOCALIZED_URL_FIELDS = new Set(["subtitlesUrl", "transcriptUrl"]);
/**
 * `LocalizedUrlSchema`'s reading, applied to every per-language media field of a course, wherever it sits.
 *
 * The schema applies it only when the WHOLE course validates. A draft with an unfinished field is stored as
 * written (`sanitizeDraftContent` in the gateway), and the build reads what the save stored (2026-10-13) — so
 * without this, one legacy subtitle gave the Italian package no track when every field was filled, and the
 * English track when an unrelated field was empty (independent review of PR #374, finding 6, measured).
 * Returns a copy; the input is untouched.
 */
export function withLegacyLocalizedUrlsRead(content) {
    const read = (node) => {
        if (Array.isArray(node))
            return node.map(read);
        if (node === null || typeof node !== "object")
            return node;
        const out = {};
        for (const [key, value] of Object.entries(node)) {
            out[key] = LOCALIZED_URL_FIELDS.has(key) ? readLegacyLocalizedUrl(value) : read(value);
        }
        return out;
    };
    return read(content);
}
/**
 * Read one language's media reference out of a localized media field.
 *
 * THE single per-language read, shared by the packager's flatten and the XML
 * emitter so the two can never disagree about what a language resolves to.
 *
 * Deliberately NOT the emitter's `pick()` helper: that one THROWS when a language
 * is absent, which is correct for a required translation and would turn "this
 * video has no Italian subtitle" into a 500.
 *
 * A plain `string` is accepted and returned as-is. That is the legacy shape from
 * before per-language media (and what `flattenLocalizedMedia` leaves behind), so
 * this function is safe to call on either.
 *
 * **Never falls back to another language.** Returning `en` for a missing `it`
 * would reproduce the exact defect this whole change exists to remove: a
 * per-language path serving identical English bytes, with every export gate green.
 */
export function pickLocalizedUrl(field, language) {
    if (field === undefined)
        return undefined;
    if (typeof field === "string")
        return field === "" ? undefined : field;
    const value = field[language];
    return value === undefined || value === "" ? undefined : value;
}
/**
 * The blocking-section three-state gate. Per catalogue §1.5:
 *  - `true`  — padlock until video/interaction completes
 *  - `false` — arrow always shown; completion not required
 *  - omitted — no gate, the next section flows inline
 */
export const BlockingSectionStateSchema = z.union([
    z.literal("true"),
    z.literal("false"),
]);
/** The blocking-section trio every layout carries. Per catalogue §1.5. */
export const BlockingSectionSchema = z
    .object({
    /** Omitted = the "empty" third state (no gate at all). */
    state: BlockingSectionStateSchema.optional(),
    color: ColorSchema.optional(),
    textColor: ColorSchema.optional(),
})
    .strict();
/**
 * A single tooltip parameter slot exposed by an HTML quiz template.
 *
 * Templates (e.g. the prototype's phishing-email mockups) carry
 * `{{slotName}}` placeholders inside their HTML; each placeholder is one
 * `TooltipParam`. Authors fill in per-language tooltip text via the
 * `tooltipValues` map on the corresponding `QuizQuestion` row; the
 * build-step substitutes `{{name}}` with the per-language value at export.
 *
 * The template registry itself is not yet a schema concern — it lives in
 * the prototype (`html-templates.jsx > HTML_TEMPLATES`) and will move to
 * a build-time asset when the production Editor lands. Cross-field
 * validation that `tooltipValues` keys match a referenced template's
 * `tooltipParams[].name` set is the gateway's job (it has registry
 * access); the schema stays permissive.
 */
export const TooltipParamSchema = z
    .object({
    name: z.string().min(1),
    label: LocalizedStringSchema,
    description: LocalizedStringSchema,
})
    .strict();
/**
 * A template's full slot vocabulary — what `html_template.tooltip_params`
 * stores and the templates route + export read back. Exported as a ready
 * array schema so zod-less consumers (the gateway) can validate stored rows.
 */
export const TooltipParamsSchema = z.array(TooltipParamSchema);
//# sourceMappingURL=shared.js.map